import { DispatchContext } from 'contexts/dispatch.context';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Dispatch } from 'redux';
import type { AlertSeverity, UserNotification } from 'state/notifications/notification.model';
import { getUserNotifications } from 'state/notifications/notification.selector';
import { showNotificationThunk } from 'state/notifications/notification.thunk';
import type { MakePartial } from 'types/utils';
import { getNewGuid } from 'utils/guid';

import { useAppSelector } from './useAppSelector';

export type PartialUserNotification = Omit<MakePartial<UserNotification, 'closeAutomatically'>, 'id' | 'level'>;
export type UserNotifier = {
  error: (notification: PartialUserNotification) => string;
  warn: (notification: PartialUserNotification) => string;
  success: (notification: PartialUserNotification) => string;
  info: (notification: PartialUserNotification) => string;
};

export function buildUserNotification(dispatch: Dispatch<any>): UserNotifier {
  return {
    error: showNotificationWithSeverity(dispatch, 'danger'),
    warn: showNotificationWithSeverity(dispatch, 'warning'),
    success: showNotificationWithSeverity(dispatch, 'success'),
    info: showNotificationWithSeverity(dispatch, 'info'),
  } as UserNotifier;
}

function showNotificationWithSeverity(dispatch: Dispatch<any>, level: AlertSeverity) {
  return (notification: PartialUserNotification) => {
    const id = getNewGuid();
    dispatch(
      showNotificationThunk({
        ...notification,
        id,
        level,
        closeAutomatically: notification.closeAutomatically ?? true,
      }),
    );
    return id;
  };
}

export function useUserNotification(): UserNotifier {
  const dispatch = useContext(DispatchContext)!;
  return useMemo(() => buildUserNotification(dispatch), [dispatch]);
}
declare type notifier = (notification: PartialUserNotification) => string;

export function useNotificationCallback(
  severity: AlertSeverity,
  { onClose, onOpen }: { onClose?: () => void; onOpen?: () => void },
): notifier {
  const dispatch = useContext(DispatchContext)!;
  const notifications = useAppSelector(getUserNotifications);
  const [subscribedNotification, setSubscribedNotification] = useState<string>();
  useEffect(() => {
    if (!!subscribedNotification && !notifications.some(notification => notification.id === subscribedNotification)) {
      setSubscribedNotification(undefined);
      onClose?.();
    }
  }, [subscribedNotification, notifications, onClose]);

  const wrapNotifier = useCallback(
    (notifier: notifier) => {
      return (notification: PartialUserNotification) => {
        const id = notifier(notification);
        setSubscribedNotification(id);
        onOpen?.();
        return id;
      };
    },
    [onOpen],
  );

  return wrapNotifier(showNotificationWithSeverity(dispatch, severity));
}
